import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { BsFormOption } from 'bs-components';
import { chain, sortBy } from 'lodash';
import { Observable, switchMap } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ModeFull } from '../models/mode-full.model';
import { Mode } from '../models/mode.model';
import { State } from '../models/state.model';
import { EnvironmentService } from './environment.service';
import { MessageHandlerService } from './messageHandler.service';

@Injectable({
  providedIn: 'root'
})
export class ModeService {
  private readonly http = inject(HttpClient);
  private readonly messageService = inject(MessageHandlerService);
  private readonly translate = inject(TranslateService);
  readonly environmentService = inject(EnvironmentService);

  private readonly _currentEnvModes$: WritableSignal<Mode[]> = signal([]);
  private readonly _allAccessibleModes$: WritableSignal<Mode[]> = signal([]);
  public readonly allAccessibleModeOptions$: Observable<BsFormOption[]> = toObservable(computed(() => this.getGroupByEnvironmentModeOptions()));

  private readonly serviceUrl: string = environment.baseUrl + '/modes';

  constructor() {
    this.environmentService.environmentChanged$.pipe(switchMap(() => this.fetchAllModes())).subscribe();
  }

  // --------------------------------------------- HTTP FUNCTIONS ---------------------------------------------------------- //

  public fetchAllModes(): Observable<Mode[]> {
    return this.http.get<Mode[]>(this.serviceUrl, { params: { includingLinkedEnvs: true } }).pipe(
      tap((modes) => {
        let currentEnvModes = modes.filter((m) => m.environment?.id == this.environmentService.currentEnvironment$()?.id);
        this._currentEnvModes$.set(currentEnvModes);
        this._allAccessibleModes$.set(modes);
      }),
      catchError(this.messageService.handleError<Mode[]>(this.translate.instant('LOGS.ACTIVITIES.GET_ALL_FAILURE')))
    );
  }

  public createMode(name: string): Observable<Mode> {
    return this.http.post<Mode>(this.serviceUrl, name, { headers: { 'Content-Type': 'application/json' } }).pipe(
      tap((mode) => {
        const modes = this.getModes();
        modes.push(mode);
        modes.sort((a: Mode, b: Mode) => {
          if (a.order > b.order) {
            return 1;
          } else if (a.order < b.order) {
            return -1;
          } else {
            return 0;
          }
        });
        this._currentEnvModes$.set(modes);

        this.messageService.log('Mode created!', this.translate.instant('LOGS.ACTIVITIES.CREATE_SUCCESS'), 1);
      }),
      catchError(this.messageService.handleError<Mode>(this.translate.instant('LOGS.ACTIVITIES.CREATE_FAILURE')))
    );
  }

  public updateModeFull(id: number, modeFull: ModeFull): Observable<ModeFull> {
    return this.http.put<ModeFull>(this.serviceUrl + '/' + id, modeFull).pipe(
      tap((updatedModeFull) => {
        if (!updatedModeFull?.mode) {
          console.warn('API response empty, using sent data for update');
          updatedModeFull = modeFull;
        }

        const modes = this.getModes();
        const updatedMode = updatedModeFull.mode;

        if (!updatedMode?.id) {
          console.error('Mode data invalid:', updatedMode);
          throw new Error('Mode data invalid: missing id');
        }

        const newModes = modes.filter((m) => updatedMode.id != m.id);
        newModes.push(updatedMode);

        const sortedModes = sortBy(newModes, (mode) => mode.order);

        this._currentEnvModes$.set(sortedModes);

        this.messageService.log('Mode updated!', this.translate.instant('LOGS.ACTIVITIES.UPDATE_SUCCESS'), 1);
      }),
      catchError(this.messageService.handleError<ModeFull>(this.translate.instant('LOGS.ACTIVITIES.UPDATE_FAILURE')))
    );
  }

  public setNewModeState(modeId: number, state: State) {
    const modes = this.getModes();
    const mode = modes.find((m) => m.id == modeId);
    if (!mode) {
      return;
    }

    const states = [...(mode.states ?? [])];
    const existingIndex = states.findIndex((s) => s.id == state.id);
    if (existingIndex >= 0) {
      states.splice(existingIndex, 1, state);
    } else {
      states.push(state);
    }

    const sortedStates = sortBy(states, (state) => state.place);

    mode.states = sortedStates;

    const newModes = modes.filter((m) => mode.id != m.id);
    newModes.push(mode);
    newModes.sort((a: Mode, b: Mode) => {
      if (a.order > b.order) {
        return 1;
      } else if (a.order < b.order) {
        return -1;
      } else {
        return 0;
      }
    });
    this._currentEnvModes$.set(newModes);
  }

  // --------------------------------------------- ACCESSORS ---------------------------------------------------------- //

  public getModes(): Mode[] {
    return this._currentEnvModes$();
  }

  private getGroupByEnvironmentModeOptions(): BsFormOption[] {
    return chain(this._allAccessibleModes$())
      .groupBy((mode) => mode.environment?.name ?? 'Unknown')
      .map((modes: Mode[], env: string) => {
        return {
          ...new BsFormOption(env),
          isParent: true,
          disabled: true,
          children: modes.map((mode) => this.toSelectOption(mode))
        };
      })
      .value();
  }

  public toSelectOption(mode: Mode) {
    return {
      id: mode.id.toString(),
      label: mode.name,
      value: mode.id
    };
  }

  public get currentEnvModes$(): Signal<Mode[]> {
    return this._currentEnvModes$.asReadonly();
  }

  public getModeByUrl(urlName: string): Mode | undefined {
    return this.currentEnvModes$().find((m) => m.urlName === urlName);
  }

  public getModeById(id: number): Mode | undefined {
    return this.currentEnvModes$().find((m) => m.id === id);
  }

  public getModeFull(id: number): Observable<ModeFull> {
    return this.http
      .get<ModeFull>(this.serviceUrl + '/' + id + '/full')
      .pipe(catchError(this.messageService.handleError<ModeFull>(this.translate.instant('LOGS.ACTIVITIES.GET_ONE_FAILURE'))));
  }
}

export interface EditModeBody {
  name: string;
}
